import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';

interface ProductivityMetricDetail {
  id: number;
  veiculo: string;
  motorista: string;
  periodo: string;
  quilometragem_percorrida: number;
  horas_trabalhadas: number;
  viagens_realizadas: number;
  consumo_medio: number;
  custo_total: number;
  eficiencia: number;
  status: string;
  data_referencia: string;
  observacoes: string;
  criado_em: string;
  atualizado_em: string;
  veiculo_info?: {
    placa: string;
    modelo: string;
    marca: string;
    ano: number;
  };
  motorista_info?: {
    nome: string;
    cpf: string;
    cnh: string;
  };
}

const ProductivityView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [metric, setMetric] = useState<ProductivityMetricDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMetric();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadMetric = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!id) {
        setError('ID não fornecido');
        return;
      }

      const response = await apiService.getProductivityMetric(parseInt(id));

      if (response.success && response.data) {
        const apiData = response.data;

        const mappedMetric: ProductivityMetricDetail = {
          id: apiData.id,
          veiculo: apiData.veiculo || apiData.veiculo_placa || 'N/A',
          motorista: apiData.motorista || apiData.motorista_nome || 'N/A',
          periodo: apiData.periodo,
          quilometragem_percorrida: apiData.quilometragem_percorrida || 0,
          horas_trabalhadas: apiData.horas_trabalhadas || 0,
          viagens_realizadas: apiData.viagens_realizadas || 0,
          consumo_medio: apiData.consumo_medio || 0,
          custo_total: apiData.custo_total || 0,
          eficiencia: apiData.eficiencia || 0,
          status: apiData.status || 'Regular',
          data_referencia: apiData.data_referencia,
          observacoes: apiData.observacoes || '',
          criado_em: apiData.criado_em || apiData.created_at,
          atualizado_em: apiData.atualizado_em || apiData.updated_at,
          veiculo_info: apiData.veiculo_info,
          motorista_info: apiData.motorista_info
        };

        setMetric(mappedMetric);
      } else {
        setError('Métrica não encontrada');
        utils.showNotification('Métrica de produtividade não encontrada', { type: 'error' });
      }
    } catch (error: any) {
      console.error('Erro ao carregar métrica de produtividade:', error);
      setError('Erro ao carregar detalhes da métrica de produtividade');
      utils.showNotification(error.message || 'Erro ao carregar detalhes da métrica de produtividade', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value);
  };

  const formatInteger = (value: number): string => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Excelente': 'status-success',
      'Bom': 'status-info',
      'Regular': 'status-warning',
      'Ruim': 'status-danger'
    };
    return statusMap[status] || 'status-secondary';
  };

  const getStatusText = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Excelente': 'Excelente',
      'Bom': 'Bom',
      'Regular': 'Regular',
      'Ruim': 'Ruim'
    };
    return statusMap[status] || status;
  };

  const getEfficiencyClass = (eficiencia: number): string => {
    if (eficiencia >= 85) return 'status-success';
    if (eficiencia >= 70) return 'status-info';
    if (eficiencia >= 60) return 'status-warning';
    return 'status-danger';
  };

  const getEfficiencyText = (eficiencia: number): string => {
    if (eficiencia >= 85) return 'Excelente';
    if (eficiencia >= 70) return 'Bom';
    if (eficiencia >= 60) return 'Regular';
    return 'Ruim';
  };

  const calculateKmPorHora = (km: number, horas: number): number => {
    return horas > 0 ? km / horas : 0;
  };

  const calculateKmPorViagem = (km: number, viagens: number): number => {
    return viagens > 0 ? km / viagens : 0;
  };

  const calculateCustoPorKm = (custo: number, km: number): number => {
    return km > 0 ? custo / km : 0;
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-secondary" onClick={() => navigate('/produtividade')}>
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
          <h1>Carregando Métrica...</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando detalhes da métrica de produtividade...</p>
        </div>
      </div>
    );
  }

  if (error || !metric) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-secondary" onClick={() => navigate('/produtividade')}>
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
          <h1>Erro ao Carregar</h1>
        </div>
        <div className="page-content">
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#e74c3c', marginBottom: '20px' }}></i>
              <h3>{error || 'Métrica não encontrada'}</h3>
              <p>A métrica de produtividade solicitada não pôde ser carregada.</p>
              <button className="btn btn-primary" onClick={() => navigate('/produtividade')}>
                Voltar para Lista
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const kmPorHora = calculateKmPorHora(metric.quilometragem_percorrida, metric.horas_trabalhadas);
  const kmPorViagem = calculateKmPorViagem(metric.quilometragem_percorrida, metric.viagens_realizadas);
  const custoPorKm = calculateCustoPorKm(metric.custo_total, metric.quilometragem_percorrida);

  return (
    <div className="page-container">
      <div className="page-header">
        <button className="btn btn-secondary" onClick={() => navigate('/produtividade')}>
          <i className="fas fa-arrow-left"></i>
          Voltar
        </button>
        <h1>Detalhes da Produtividade</h1>
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={() => navigate(`/produtividade/editar/${metric.id}`)}
          >
            <i className="fas fa-edit"></i>
            Editar
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Cabeçalho com informações principais */}
        <div className="card">
          <div className="card-header">
            <h2>Métrica de Produtividade #{metric.id}</h2>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span className={`status-badge ${getStatusClass(metric.status)}`}>
                {getStatusText(metric.status)}
              </span>
              <span className={`status-badge ${getEfficiencyClass(metric.eficiencia)}`}>
                {metric.eficiencia}% - {getEfficiencyText(metric.eficiencia)}
              </span>
              <span className="badge badge-secondary">
                {metric.periodo}
              </span>
            </div>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label>Período:</label>
                <span className="info-value">{metric.periodo}</span>
              </div>
              <div className="info-item">
                <label>Data de Referência:</label>
                <span className="info-value">{formatDate(metric.data_referencia)}</span>
              </div>
              <div className="info-item">
                <label>Veículo:</label>
                <span className="info-value">
                  <strong>{metric.veiculo}</strong>
                  {metric.veiculo_info && (
                    <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                      {metric.veiculo_info.marca} {metric.veiculo_info.modelo} ({metric.veiculo_info.ano})
                    </div>
                  )}
                </span>
              </div>
              <div className="info-item">
                <label>Motorista:</label>
                <span className="info-value">
                  {metric.motorista}
                  {metric.motorista_info && (
                    <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                      CNH: {metric.motorista_info.cnh}
                    </div>
                  )}
                </span>
              </div>
              <div className="info-item">
                <label>Quilometragem Percorrida:</label>
                <span className="info-value">
                  <strong>{formatInteger(metric.quilometragem_percorrida)} km</strong>
                </span>
              </div>
              <div className="info-item">
                <label>Horas Trabalhadas:</label>
                <span className="info-value">{formatInteger(metric.horas_trabalhadas)} horas</span>
              </div>
              <div className="info-item">
                <label>Viagens Realizadas:</label>
                <span className="info-value">{formatInteger(metric.viagens_realizadas)}</span>
              </div>
              <div className="info-item">
                <label>Consumo Médio:</label>
                <span className="info-value">{formatNumber(metric.consumo_medio)} km/L</span>
              </div>
              <div className="info-item">
                <label>Custo Total:</label>
                <span className="info-value">
                  <strong style={{ color: '#04C64F' }}>
                    {formatCurrency(metric.custo_total)}
                  </strong>
                </span>
              </div>
              <div className="info-item">
                <label>Eficiência:</label>
                <span className="info-value">
                  <span className={`status-badge ${getEfficiencyClass(metric.eficiencia)}`}>
                    {metric.eficiencia}%
                  </span>
                </span>
              </div>
              <div className="info-item">
                <label>Status:</label>
                <span className="info-value">
                  <span className={`status-badge ${getStatusClass(metric.status)}`}>
                    {getStatusText(metric.status)}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Estatísticas detalhadas */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Estatísticas Detalhadas</h3>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label>KM por Hora:</label>
                <span className="info-value">{formatNumber(kmPorHora)} km/h</span>
              </div>
              <div className="info-item">
                <label>KM por Viagem:</label>
                <span className="info-value">{formatNumber(kmPorViagem)} km/viagem</span>
              </div>
              <div className="info-item">
                <label>Custo por KM:</label>
                <span className="info-value">{formatCurrency(custoPorKm)}/km</span>
              </div>
              <div className="info-item">
                <label>Horas por Viagem:</label>
                <span className="info-value">
                  {metric.viagens_realizadas > 0 ? formatNumber(metric.horas_trabalhadas / metric.viagens_realizadas) : 0} h/viagem
                </span>
              </div>
              <div className="info-item">
                <label>Viagens por Dia (média):</label>
                <span className="info-value">
                  {metric.horas_trabalhadas > 0 ? formatNumber((metric.viagens_realizadas / metric.horas_trabalhadas) * 8) : 0} viagens/dia
                </span>
              </div>
              <div className="info-item">
                <label>Eficiência Energética:</label>
                <span className="info-value">
                  {formatNumber(metric.quilometragem_percorrida / metric.custo_total)} km/R$
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Observações */}
        {metric.observacoes && (
          <div className="card" style={{ marginTop: '20px' }}>
            <div className="card-header">
              <h3>Observações</h3>
            </div>
            <div className="card-body">
              <p style={{ color: '#7f8c8d', lineHeight: '1.6', margin: 0 }}>
                {metric.observacoes}
              </p>
            </div>
          </div>
        )}

        {/* Informações de auditoria */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Informações de Auditoria</h3>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label>Criado em:</label>
                <span className="info-value">{formatDateTime(metric.criado_em)}</span>
              </div>
              <div className="info-item">
                <label>Atualizado em:</label>
                <span className="info-value">{formatDateTime(metric.atualizado_em)}</span>
              </div>
              <div className="info-item">
                <label>ID da Métrica:</label>
                <span className="info-value">{metric.id}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Ações */}
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/produtividade')}
          >
            <i className="fas fa-list"></i>
            Voltar para Lista
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => navigate(`/produtividade/editar/${metric.id}`)}
          >
            <i className="fas fa-edit"></i>
            Editar Métrica
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductivityView;
